import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import moment from 'moment-timezone';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

function AvailabilitySection({ availabilityData, isGlobal = false }) {
  // If showing plant-specific availability, render stacked area chart over the whole year (no arrows)
  const chartData = useMemo(() => {
    if (isGlobal) return [];
    const byDay = {};
    (availabilityData || []).forEach(item => {
      const d = moment(item.startDate);
      if (!d.isValid()) return;
      const key = d.format('YYYY-MM-DD');
      const avail = Math.max(0, Number(item.availableCapacity) || 0);
      const unavail = Math.max(0, Number(item.unavailableCapacity) || 0);
      if (!byDay[key]) byDay[key] = { date: key, available: 0, unavailable: 0 };
      byDay[key].available += avail;
      byDay[key].unavailable += unavail;
    });
    return Object.values(byDay).sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [availabilityData, isGlobal]);

  const columns = [
    {
      title: isGlobal ? 'Plant' : 'Reactor',
      dataIndex: isGlobal ? 'plantName' : 'reactorName',
      key: isGlobal ? 'plantName' : 'reactorName',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
    },
    {
      title: 'Available Capacity (MW)',
      dataIndex: 'availableCapacity',
      key: 'availableCapacity',
    },
    {
      title: 'Unavailable Capacity (MW)',
      dataIndex: 'unavailableCapacity',
      key: 'unavailableCapacity',
    },
  ];

  if (!isGlobal) {
    if (!Array.isArray(chartData) || chartData.length === 0) {
      return (
        <div className="AvailabilitySection">
          <h3>Plant Availability</h3>
          <div>no unavailability data provided</div>
        </div>
      );
    }

    const maxCap = chartData.reduce(
      (m, d) => Math.max(m, (d.available || 0) + (d.unavailable || 0)),
      0,
    );
    const tickFormatter = v => {
      const n = Number(v) || 0;
      return (n / 1000).toFixed(1); // show in GW
    };
    const tooltipFormatter = (value, name) => [
      `${((Number(value) || 0) / 1000).toFixed(2)} GW`,
      name === 'available' ? 'Available' : 'Unavailable',
    ];
    const dateFormatter = v => moment(v).format('DD/MM');
    return (
      <div className="AvailabilitySection">
        <h3>Plant Availability</h3>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id="availFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#26a65b" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#26a65b" stopOpacity={0.2} />
                </linearGradient>
                <linearGradient id="unavailFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f39c12" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#f39c12" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={dateFormatter}
                minTickGap={20}
              />
              <YAxis
                tickFormatter={tickFormatter}
                domain={[0, Math.ceil(maxCap / 100) * 100]}
                label={{ value: 'GW', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                formatter={tooltipFormatter}
                labelFormatter={v => moment(v).format('YYYY-MM-DD')}
              />
              <Area
                type="monotone"
                dataKey="unavailable"
                stackId="1"
                stroke="#f39c12"
                fillOpacity={1}
                fill="url(#unavailFill)"
              />
              <Area
                type="monotone"
                dataKey="available"
                stackId="1"
                stroke="#26a65b"
                fillOpacity={1}
                fill="url(#availFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Global: keep table view
  const formattedData = availabilityData.map(item => {
    const round1 = v =>
      v === null || v === undefined || Number.isNaN(Number(v))
        ? null
        : Number.parseFloat(Number(v).toFixed(1));
    return {
      ...item,
      startDate: moment(item.startDate).format('DD/MM/YYYY'),
      endDate: item.endDate ? moment(item.endDate).format('DD/MM/YYYY') : 'N/A',
      availableCapacity: round1(item.availableCapacity),
      unavailableCapacity: round1(item.unavailableCapacity),
    };
  });
  return (
    <div className="AvailabilitySection">
      <h3>US Nuclear Availability</h3>
      <Table
        dataSource={formattedData}
        columns={columns}
        rowKey={record =>
          record.id ||
          `${record.plantName || record.reactorName || 'item'}_${
            record.startDate
          }_${record.endDate}`
        }
        pagination={formattedData.length > 10}
      />
    </div>
  );
}

AvailabilitySection.propTypes = {
  availabilityData: PropTypes.array.isRequired,
  isGlobal: PropTypes.bool,
  currentDate: PropTypes.string,
};

export default AvailabilitySection;
